import api from '../utils/api';

export interface DashboardStats {
  certificatesIssued: {
    count: number;
    change: number;
    trend: 'up' | 'down';
  };
  jobsCompleted: {
    count: number;
    change: number;
    trend: 'up' | 'down';
  };
  certificatesExpiring: {
    count: number;
    overdue: number;
  };
  jobCompletionRate: {
    rate: number;
    change: number;
    trend: 'up' | 'down';
  };
}

export interface JobBreakdown {
  type: string;
  count: number;
  percentage: number;
  color: string;
}

export interface JobTimelineData {
  day: string;
  scheduled: number;
  inProgress: number;
  completed: number;
}

export interface RecentJob {
  id: string;
  title: string;
  type: string;
  client: {
    name: string;
    address: string;
  };
  engineer: {
    name: string;
  };
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED';
  scheduledDate: string;
  value: number;
}

export interface CertificateCompliance {
  dueThisMonth: number;
  awaitingSignature: number;
}

export interface DashboardData {
  stats: DashboardStats;
  jobBreakdown: JobBreakdown[];
  jobTimeline: JobTimelineData[];
  recentJobs: RecentJob[];
  certificateCompliance: CertificateCompliance;
  isEmpty: {
    certificatesIssued: boolean;
    jobsCompleted: boolean;
    certificatesExpiring: boolean;
    jobBreakdown: boolean;
    jobTimeline: boolean;
    recentJobs: boolean;
    certificateCompliance: boolean;
  };
}

export type DashboardTabKey = 'today' | 'attention' | 'upcoming' | 'cash' | 'compliance';

export type DashboardSignalSeverity = 'low' | 'medium' | 'high';

export type DashboardSignal = {
  id: string;
  tab: DashboardTabKey;
  severity: DashboardSignalSeverity;
  title: string;
  subtitle?: string;
  actionLabel: string;
  actionRoute: string;
  entity?: {
    jobId?: string;
    invoiceId?: string;
    certificateId?: string;
    quoteId?: string;
    clientId?: string;
  };
  meta?: Record<string, any>;
};

export type CertOperatorState = {
  sentToday: number;
  missingData: number;
  needsReview: number;
  expiredCertificates: number;
  expiringUnrenewed: number;
};

export type DashboardOverview = {
  kpis: {
    jobsNeedingAction: number;
    certificatesAtRisk: number;
    moneyStuckTotal: number;
    automationOpportunities: number;
  };
  jobPipeline: {
    scheduled: number;
    inProgress: number;
    overdue: number;
    completed: number;
  };
  tabCounts: Record<DashboardTabKey, number>;
  certOperator?: CertOperatorState;
};

class DashboardService {
  async getDashboardData(): Promise<DashboardData> {
    try {
      console.log('🔍 Fetching dashboard data from:', api.defaults.baseURL + '/dashboard');

      const response = await api.get('/dashboard');
      console.log('✅ Dashboard data received:', response.data);
      console.log('✅ Raw response structure:', JSON.stringify(response.data, null, 2));

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch dashboard data:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Request setup error:', error.message);
      }
      throw error;
    }
  }

  async getOverview(): Promise<DashboardOverview> {
    try {
      const response = await api.get('/dashboard/overview');

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch dashboard overview:', error);
      throw error;
    }
  }

  async getTab(tabKey: DashboardTabKey): Promise<DashboardSignal[]> {
    try {
      const response = await api.get(`/dashboard/tab/${tabKey}`);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to fetch dashboard tab:', error);
      throw error;
    }
  }

  async getJobBreakdown(): Promise<JobBreakdown[]> {
    try {
      const response = await api.get('/dashboard/job-breakdown');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch job breakdown:', error);
      throw error;
    }
  }

  async getJobTimeline(period: 'week' | 'month' = 'week'): Promise<JobTimelineData[]> {
    try {
      const response = await api.get(`/dashboard/job-timeline?period=${period}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch job timeline:', error);
      throw error;
    }
  }

  async getRecentJobs(limit: number = 10): Promise<RecentJob[]> {
    try {
      const response = await api.get(`/dashboard/recent-jobs?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch recent jobs:', error);
      throw error;
    }
  }

  async getCertificateCompliance(): Promise<CertificateCompliance> {
    try {
      const response = await api.get('/dashboard/certificate-compliance');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch certificate compliance:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();
